import {z} from "zod";
import {TFunction} from "i18next";
import {FORM_FIELD_TYPE} from "@lib/Form/FormModels";
import {JField} from "@lib/Finalization/FinalizationFormTypes";
import {MsalIdTokenClaims} from "@store/AuthStore";
import {extractLabelFromPipe} from "@commons/Utils";

export const MIN_LENGTH_VAT = 4;
export const MIN_LENGTH_BUSINESS_NAME = 4;

export type OnboardFormValues = Record<string, string>;

export interface TokenFieldDef {
    fromClaims?: (claims: MsalIdTokenClaims, derivedCountry?: string | null) => string | undefined;
}

/**
 * Mappa hurlId → definizione del campo token (estrazione dai claim).
 *
 * Usata in due momenti:
 *  1. Pre-compilazione del form (defaultValues): popola il valore iniziale di ogni
 *     campo visibile il cui hurlId è qui presente, se il dato è nel token.
 *  2. Valorizzazione dei campi hidden durante il submit: recupera il dato dal
 *     claim per i campi che l'utente non tocca.
 */
export const TOKEN_FIELDS: Record<string, TokenFieldDef> = {
    name: {fromClaims: (c) => c.given_name},
    surname: {fromClaims: (c) => c.family_name},
    email: {fromClaims: (c) => c["signInNames.emailAddress"]},
    phone: {fromClaims: (c) => c.mobile},
    user_type: {
        fromClaims: (c) => c.extension_consumerOrProfessional
    },
    country_raw: {
        fromClaims: (c) => c.country ?? c.extension_ADB2C_CompanyCountry
    },
    country_code: {
        fromClaims: (_c, derivedCountry) => derivedCountry ?? undefined
    },
    adress_address: {
        fromClaims: (c) => c.streetAddress ?? c.extension_ADB2C_CompanyAddress
    },
    address_zipcode: {
        fromClaims: (c) => c.extension_ADB2C_CompanyZIP ?? c.postalCode
    },
    address_city: {
        fromClaims: (c) => c.city ?? c.extension_ADB2C_CompanyCity
    },
    address_state: {
        fromClaims: (c) => extractLabelFromPipe(c.state) ?? extractLabelFromPipe(c.extension_ADB2C_CompanyState) ?? undefined
    },
    address_country: {
        fromClaims: (c) => extractLabelFromPipe(c.country) ?? extractLabelFromPipe(c.extension_ADB2C_CompanyCountry) ?? undefined
    },
    gewiss_upn: {fromClaims: (c) => c.upn},
    piva: {fromClaims: (c) => c.extension_ADB2C_VAT},
    business_name: {fromClaims: (c) => c.extension_ADB2C_CompanyName},
    // Nessun fromClaims: il riferimento alla foto profilo è risolto a parte nel componente.
    profilePictureRef: {},
};

export interface CmsValidationMessages {
    requiredMessage?: string;
    invalidMessage?: string;
    errorModalMessage?: string
}

/**
 * Estrae i messaggi di validazione dal campo CMS `validationMessage`, che contiene
 * una stringa JSON con le chiavi `requiredMessage`, `invalidMessage` , `errorModalMessage`.
 * se la stringa è assente, non è JSON valido, non è un oggetto o una chiave non è una stringa valorizzata, quel
 * messaggio resta `undefined` e i chiamanti ricadono sul fallback i18n.
 */
export const parseCmsValidationMessages = (raw?: string): CmsValidationMessages => {
    if (!raw) return {};

    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) return {};

        const pick = (value: unknown): string | undefined =>
            typeof value === "string" && value.trim() !== "" ? value : undefined;

        const obj = parsed as Record<string, unknown>;
        return {
            requiredMessage: pick(obj.requiredMessage),
            invalidMessage: pick(obj.invalidMessage),
            errorModalMessage: pick(obj.errorModalMessage),
        };
    } catch {
        return {};
    }
};



export const requiredMessage = (field: JField, t: TFunction, fallbackKey: string): string => {
    const messages = parseCmsValidationMessages(field.cmsConfig.validationMessage);
    return messages.requiredMessage || t(fallbackKey);
};


export const invalidMessage = (field: JField, t: TFunction, fallbackKey: string): string => {
    const messages = parseCmsValidationMessages(field.cmsConfig.validationMessage);
    return messages.invalidMessage || t(fallbackKey);
};

export const errorModalMessage = (field: JField | undefined, t: TFunction, fallbackKey: string): string => {
    const messages = parseCmsValidationMessages(field?.cmsConfig.validationMessage);
    return messages.errorModalMessage || t(fallbackKey);
};


const withOptionalString = (schema: z.ZodTypeAny, mandatory: boolean): z.ZodTypeAny =>
    mandatory ? schema : z.union([z.literal(""), schema]);


const emailValidator = (field: JField, t: TFunction): z.ZodTypeAny =>
    withOptionalString(
        z
            .string()
            .min(1, {message: requiredMessage(field, t, "public.onboard.form.validation.business_email_required")})
            .pipe(z.email({message: invalidMessage(field, t, "public.onboard.form.validation.business_email_invalid")})),
        field.mandatory,
    );

/**
 * Override di validazione per singolo `hurlId`
 */
const FIELD_OVERRIDES: Record<string, (field: JField, t: TFunction) => z.ZodTypeAny> = {
    piva: (field, t) => withOptionalString(
        z
            .string()
            .min(1, {message: requiredMessage(field, t, "public.onboard.form.validation.vat_required")})
            .min(MIN_LENGTH_VAT, {message: invalidMessage(field, t, "public.onboard.form.validation.vat_too_short")}),
        field.mandatory,
    ),
    business_name: (field, t) => withOptionalString(
        z
            .string()
            .min(1, {message: requiredMessage(field, t, "public.onboard.form.validation.business_name_required")})
            .min(MIN_LENGTH_BUSINESS_NAME, {message: invalidMessage(field, t, "public.onboard.form.validation.business_name_too_short")}),
        field.mandatory,
    ),
    business_email: (field, t) => emailValidator(field, t),
};


export const buildFieldValidator = (field: JField, t: TFunction): z.ZodTypeAny => {
    const {hurlId, mandatory, type} = field;

    const override = FIELD_OVERRIDES[hurlId];
    if (override) return override(field, t);

    switch (type) {
        case FORM_FIELD_TYPE.CHECKBOX:
        case FORM_FIELD_TYPE.PRIVACY: {
            const message = requiredMessage(field, t, "public.onboard.form.validation.gdpr_required");
            return mandatory ? z.literal("true", {message}) : z.literal(["true", "false"])
        }

        case FORM_FIELD_TYPE.EMAIL:
            return emailValidator(field, t);

        default: {
            if (!mandatory) return z.string().optional();
            const message = requiredMessage(field, t, "public.onboard.form.validation.field_required");
            return z.string().min(1, {message});
        }
    }
};

export const buildOnboardSchema = (fields: JField[], t: TFunction) => {
    const shape: Record<string, z.ZodTypeAny> = {};
    for (const field of fields) {
        shape[field.hurlId] = buildFieldValidator(field, t);
    }
    return z.object(shape);
};
